import React, { useEffect, useRef } from 'react';
import { ProductSearch } from '@features/products/components/ProductSearch/ProductSearch';
import { useQuote } from '../../context/QuoteContext';
import type { Product } from '@features/products/types/products.types';
import { PageHeader } from '@/shared/components';

interface ProductSelectionStepProps {
    /**
     * Advance-to-next-step callback supplied by QuoteWizard, bound to the
     * wizard's advance action (see useQuoteAdvance). Optional
     * so the step still renders in isolation without auto-advancing.
     */
    onAdvance?: () => void;
}

export const ProductSelectionStep: React.FC<ProductSelectionStepProps> = ({ onAdvance }) => {
    const { selectedProduct, selectProduct } = useQuote();
    // Marks a fresh user selection so the post-commit effect auto-advances only
    // on a click, not when returning to this step with a product already chosen.
    const advanceRequestedRef = useRef(false);
    const headingRef = useRef<HTMLHeadingElement>(null);

    // Recovery from NoSellingDealerError can swap this step in and unmount the focused button,
    // which would drop focus to <body>.
    useEffect(() => {
        headingRef.current?.focus();
    }, []);

    const handleProductSelect = (product: Product) => {
        advanceRequestedRef.current = true;
        selectProduct(product);
    };

    // Advance only after selectProduct is reflected in quote state. Advancing
    // synchronously inside handleProductSelect read a stale canProceed (the
    // dispatch had not yet committed), so the first click never moved the wizard.
    // Running it from this effect guarantees the shared advance guard
    // (canProceed) and nextStep see the freshly selected product.
    useEffect(() => {
        if (advanceRequestedRef.current && selectedProduct) {
            advanceRequestedRef.current = false;
            onAdvance?.();
        }
    }, [selectedProduct, onAdvance]);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap justify-between content-start items-start">
                <PageHeader
                    title="Select Product"
                    subtitle="Please select the service plan product that best fits the customer's needs."
                    headingRef={headingRef}
                    headingTabIndex={-1}
                />
            </div>
            <ProductSearch
                onSelectProduct={handleProductSelect}
                selectedProduct={selectedProduct}
            />
        </div>
    );
};
